/**
 * FreshCards — main entry point.
 * The Mantine theme is derived from the active App-Overlay
 * (V4 § 2.1.6). The slate accent flows through cssVariablesResolver
 * so every Mantine component inherits the skin without re-bundling.
 */
import React from 'react';
import ReactDOM from 'react-dom/client';
import { MantineProvider, createTheme, type CSSVariablesResolver } from '@mantine/core';
import App from './App';
import './index.css';
import './ui/styles/surface.css';
import '@mantine/core/styles.css';
import '@blocknote/core/style.css';
import '@blocknote/mantine/style.css';
import { initializeStorage } from './core/storage-adapter';

// Initialize storage adapters before app renders
initializeStorage().catch(console.error);

// Base Mantine theme. Brand-aware colors are applied via cssVariablesResolver
// below, reading from the active skin's CSS custom properties (which the
// ThemeProvider sets on <html> based on app-overlays/<overlay>/accent.json).
const theme = createTheme({
  primaryColor: 'slate',
  primaryShade: 6,
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  colors: {
    // 'slate' is a custom palette. Mantine expects 10 shades (0-9).
    // We read these at runtime from --fc-accent-* but Mantine needs concrete
    // values at build time. We approximate with the slate-600 family.
    slate: [
      '#f8fafc', // 0 — slate-50
      '#f1f5f9', // 1 — slate-100
      '#e2e8f0', // 2 — slate-200
      '#cbd5e1', // 3 — slate-300
      '#94a3b8', // 4 — slate-400
      '#64748b', // 5 — slate-500
      '#475569', // 6 — slate-600 ← primary
      '#334155', // 7 — slate-700
      '#1e293b', // 8 — slate-800
      '#0f172a', // 9 — slate-900
    ],
  },
});

// Resolver that derives Mantine CSS variables from the active skin.
// This is what actually picks up the runtime --fc-accent-primary (slate-600)
// so Mantine's <Button>, <Badge>, etc. render in the skin's accent color.
const cssVariablesResolver: CSSVariablesResolver = () => ({
  variables: {
    '--mantine-primary-color-filled': 'var(--fc-accent-primary, #475569)',
    '--mantine-primary-color-filled-hover': 'var(--fc-accent-hover, #334155)',
    '--mantine-primary-color-light': 'var(--fc-accent-muted, #e2e8f0)',
    '--mantine-primary-color-light-hover': 'var(--fc-accent-muted, #e2e8f0)',
    '--mantine-primary-color-light-color': 'var(--fc-accent-primary, #475569)',
    '--mantine-primary-color-contrast': 'var(--fc-text-inverse, #ffffff)',
  },
});

const root = ReactDOM.createRoot(document.getElementById('root')!);

root.render(
  <React.StrictMode>
    <MantineProvider
      theme={theme}
      defaultColorScheme="auto"
      cssVariablesResolver={cssVariablesResolver}
    >
      <App />
    </MantineProvider>
  </React.StrictMode>,
);
